// tslint:disable: cyclomatic-complexity

import React from 'react';
import { TreeList, Column } from 'devextreme-react/tree-list';
import ArrayStore from 'devextreme/data/array_store';
import DataSource from 'devextreme/data/data_source';
import { isNull } from 'utils/utils';
import { showSnackBar } from 'components/common/SnackBars/SnackBar.hooks';
import { isScreenRequest } from 'api/utils';
import 'devextreme/dist/css/dx.light.css';
import './CataloguePartsGrid.css';
import MoreIcon from '@markinson/uicomponents-v2/SvgIcons/More';
import { useRetrieveCataloguePartsChildrenList, useRetrieveCataloguePartsList, useRetrievePricingAndAvailability } from 'api/Worksale/AutoInfoCatalogue/autoInfoCatalogue';
import { useVRContextSelector } from '../VehicleSearch/VehicleSearchContext';
import { useSPContextSelector } from '../../Worksale.context';
import Selectors from '../../Worksale.selectors';
import { useScrollRestoration } from '../ScrollPositionContext';
import { LoadPanel } from 'devextreme-react/load-panel';
import { COLUMN_SIZE, DATA_GRID_LOADING_INDICATOR_HEIGHT_WIDTH } from 'components/common/DataGridDevEx/DataGrid.constants';
import { useValidateProduct } from 'api/Worksale/worksaleLatest';
import { useCalculateFastLineDetail, useCreateFastLine, useCurrentWorksale } from 'api/Worksale/catalogue';
import AddToSale from 'components/Worksale/ProductCatalogue/AddToSale';
import { IAddToSaleHandle } from '../../ProductCatalogue/AddToSale/AddToSale.properties';
import { usePriceOverrideAndKitValidations } from '../../Worksale.hooks';
import PriceOverrideAuthorityDialog from 'components/common/PriceOverrideAuthorityDialog';
import BOMDialog from 'components/common/BOMDialog';
import { ICalculateFastLineCriteriaFacade, ICreateFastLineFacade } from 'api/swaggerTypes';
import { IPriceOverrideData } from 'components/common/PriceOverrideAuthorityDialog/PriceOverrideAuthorityDialog.properties';

const MAX_CELL_INDEX = 3;
const DELAY = 50;
const NAME_COLSPAN = 2;
const EXPAND_DELAY = 100;

const Catalogue = (props: any) => {
  const { handleVehicleDetailsOnClick, allowPriceQuantityDialogAutoi, errorHandler } = props;

  const fetchCataloguePartsListMutation = useRetrieveCataloguePartsList(errorHandler);
  const fetchCataloguePartsChildrenListMutation = useRetrieveCataloguePartsChildrenList(errorHandler);
  const fetchPricingAndAvailabilityMutation = useRetrievePricingAndAvailability(errorHandler);
  const treeListRef = React.useRef<any>(null);

  const {
          multipleBomOpen,
          bomList,
          kitStatus,
          priceOverrideDialogOpen,
          priceOverrideScreenType,
          priceOverrideDetailSummary,
          selectedBomCode,
          validate,
          // resetStates: resetPriceOverrideBomStates,
          isPriceOverrideScreenRequest,
          setKitStatus,
          setSelectedBomCode,
          setMultipleBomState,
          setPriceOverrideDialogOpen,
          setPriceOverrideDetailSummary
      } = usePriceOverrideAndKitValidations();

  // ----------------------
  // DataSource + local mutable cache (productsRef)
  // We use a stable DataSource instance so DevExtreme TreeList does NOT reinitialize
  // when React re-renders due to mutation state changes.
  // ----------------------
  const [dataSource] = React.useState(
    () =>
      new DataSource({
        store: new ArrayStore({
          key: 'guid',
          data: [],
        }),
      })
  );

  // Local mutable cache to avoid using React state for the product items;
  // we update the DataSource when cache changes.
  const productsRef = React.useRef<any[]>([]);

  const refreshDataSource = React.useCallback(
    (newData: any[]) => {
      // update local cache
      productsRef.current = newData;

      // replace the underlying store array in a safe manner
      const store = dataSource.store() as any;
      // ArrayStore doesn't have a direct "set all" API; easiest is to replace internal array via clear/insert sequence
      // Clear existing items:
      try {
        // ArrayStore provides clear() in some versions; otherwise use load() later to refresh
        if (typeof store.clear === 'function') {
          store.clear();
        } else if (Array.isArray((store as any)._array)) {
          (store as any)._array.length = 0;
        }
      } catch (err) {
        // fallback: attempt to reload with new ArrayStore
      }

      // Insert new data
      if (Array.isArray(newData) && newData.length > 0) {
        try {
          newData.forEach((item) => {
            store.insert(item);
          });
        } catch (err) {
          // If insert fails, rebuild store entirely (fallback)
          const newStore = new ArrayStore({ key: 'guid', data: newData });
          (dataSource as any).store(newStore);
        }
      }

      // Reload datasource so TreeList sees the new items
      try {
        dataSource.reload();
      } catch (err) {
        // fallback: set new store and reload
        (dataSource as any).store(new ArrayStore({ key: 'guid', data: newData }));
        dataSource.reload();
      }
    },
    [dataSource]
  );

  // ----------------------
  // Other local UI state that shouldn't re-create TreeList
  // ----------------------
  const [expandedNodes, setExpandedNodes] = React.useState<Set<number | string>>(new Set());
  const selectedCatalogueEntry = useVRContextSelector<'SelectedCatalogueEntry'>((state) => state.SelectedCatalogueEntry);
  const CatalogueTypeId = useVRContextSelector<'CatalogueTypeId'>((state) => state.CatalogueTypeId);
  const VehicleId = useVRContextSelector<'VehicleId'>((state) => state.VehicleId);
  const customerId = useSPContextSelector(Selectors.CustomerId);
  const validateProductMutation = useValidateProduct();
  const { data: worksaleData } = useCurrentWorksale();
  const calculateFastLineDetailMutation = useCalculateFastLineDetail();
  const createFastLineMutation = useCreateFastLine();
  const [addToSaleOpen, setAddToSaleOpen] = React.useState(false);
  const [selectedRowDetails, setSelectedRowDetails] = React.useState(null);
  const addSaleRef = React.useRef<IAddToSaleHandle>(null);
  const quantityMapRef = React.useRef<Record<string | number, number>>({});

  // Scroll position management
  const isDataLoading = React.useMemo(
    () => {
      return fetchCataloguePartsListMutation?.isLoading || false;
    },
    [fetchCataloguePartsListMutation?.isLoading]);

  const {
    saveScrollPosition,
    resetScrollState
  } = useScrollRestoration({
    treeListRef,
    dataLength: productsRef.current.length,
    isLoading: isDataLoading
  });

  const fastLineInlineData = React.useMemo(
    () => {
      if (selectedRowDetails) {

        return calculateFastLineDetailMutation.data?.FastLineDetails;
      }

      return null;
    },
    [selectedRowDetails, calculateFastLineDetailMutation.data?.FastLineDetails]
  );

  const [expandedRowKeys, setExpandedRowKeys] = React.useState<(string | number)[]>([]);

  // ----------------------
  // Notifications
  // ----------------------
  const notifyProductFailedToAdd = React.useCallback(
    () => {
      showSnackBar({
        variant: 'error',
        message: 'Failed to add product',
      });
    },
    []
  );

  const notifyProductAdded = React.useCallback(
    () => {
      showSnackBar({
        variant: 'success',
        message: 'Product added successfully',
      });
    },
    []
  );

  // ----------------------
  // fetchCataloguePartsList (uses DataSource instead of setProducts)
  // ----------------------
  const fetchCataloguePartsList = React.useCallback(
    async () => {
      const data = await fetchCataloguePartsListMutation.mutateAsync({
        CatalogueType: CatalogueTypeId,
        VehicleId: VehicleId,
        CatalogueId: selectedCatalogueEntry?.PartGroupID || 0,
        urlQueryParams: { SubGroupId: selectedCatalogueEntry?.SubGroupID || 0 },
      });

      const parts = data?.Parts ?? [];

      // update our local cache and DataSource immediately (show raw parts)
      refreshDataSource(parts);

      // keep rootParts local for parallel enrichments
      const rootParts = parts.filter((p) => p.ParentId === 0);

      // Run all root fetches in parallel
      const allEnrichments = await Promise.all(
        rootParts.map(async (root) => {
          const children = parts.filter((p) => p.ParentId === root.ItemId && p.ProductId);

          if (children.length === 0) return [];

          const pricingRequest = children.map((child) => ({
            ProductId: child.ProductId,
            Quantity: child.PCQ || 1,
          }));

          const pricingResponse = await fetchPricingAndAvailabilityMutation.mutateAsync({
            ProductList: pricingRequest,
            urlQueryParams: { CustomerId: customerId, Warehouse: worksaleData?.Summary?.SaleHeader?.WarehouseEntity, WorksaleId: worksaleData?.WorksaleId },
          });

          const pricingMap = new Map((pricingResponse?.ProductDetails ?? []).map((p) => [String(p.ProductId), p]));

          return children.map((child) => {
            const pricingData = pricingMap.get(String(child.ProductId)) as any;

            return {
              ...child,
              price: pricingData?.ExtendedIncDisplay ?? null,
              retail: pricingData?.RetailPriceDisplay ?? null,
              other: pricingData?.ElsewhereQuantityDisplay ?? null,
              availability: pricingData?.AvailableQuantityDisplay ?? null,
              IsValidProduct: pricingData?.IsValidProduct ?? false,
            };
          });
        })
      );

      // Flatten and merge all enriched children into products
      const enrichedFlat = (allEnrichments as any).flat();

      if (enrichedFlat.length > 0) {
        // merge enriched items into the local cache
        const merged = productsRef.current.map((p: any) => enrichedFlat.find((e: any) => e.ItemId === p.ItemId) ?? p);

        // Refresh the DataSource with merged results
        refreshDataSource(merged);
      }
    },
    [
      CatalogueTypeId,
      VehicleId,
      selectedCatalogueEntry,
      customerId,
      worksaleData?.Summary,
      refreshDataSource,
    ]
  );

  React.useEffect(
    () => {
      if (selectedCatalogueEntry && CatalogueTypeId !== -1 && VehicleId !== -1) {
        fetchCataloguePartsList();
      }
    },
    [selectedCatalogueEntry, CatalogueTypeId, VehicleId, fetchCataloguePartsList]
  );

  React.useEffect(
    () => {
      // Clear store & cache on catalogue change
      refreshDataSource([]);
      setExpandedNodes(new Set());
      setExpandedRowKeys([]);
      // Only reset scroll state when catalogue actually changes (not on component re-render)
      // Add a small delay to avoid resetting during navigation
      setTimeout(
        () => {
          const shouldRestore = localStorage.getItem('catalogueShouldRestore') === 'true';
          if (!shouldRestore) {
            resetScrollState();
          }
        },
        DELAY
      );
    },
    [selectedCatalogueEntry, refreshDataSource, resetScrollState]
  );

  // Clear parts grid when vehicle is cleared/deselected
  React.useEffect(
    () => {
      if (VehicleId === -1 || VehicleId === null || VehicleId === undefined) {
        refreshDataSource([]);
        setExpandedNodes(new Set());
        setExpandedRowKeys([]);
        // Only reset scroll state when vehicle is actually cleared (not during navigation)
        setTimeout(
          () => {
            const shouldRestore = localStorage.getItem('catalogueShouldRestore') === 'true';
            if (!shouldRestore) {
              resetScrollState();
            }
          },
          DELAY
        );
      }
    },
    [VehicleId, refreshDataSource, resetScrollState]
  );
  // Auto-expand parent rows only once after the first full data load
  React.useEffect(
    () => {
      if (!treeListRef.current) return;

      const instance = treeListRef.current.instance;
      if (!instance) return;

      const rootKeys = productsRef.current
        .filter((p) => Number(p.parentId) === 0)
        .map((p) => p.guid);

      if (rootKeys.length === 0) return;

      // Prevent re-expansion on every update (only run once)
      if (expandedRowKeys.length > 0) return;

      // Defer until TreeList is painted
      requestAnimationFrame(() => {
        rootKeys.forEach((key) => {
          try {
            instance.expandRow(key);
          } catch (err) {
            console.warn('Row expansion failed:', err);
          }
        });
      });

      setExpandedRowKeys(rootKeys);
    },
    [treeListRef.current, productsRef.current.length]
  );

  // Attach this handler above your return()
  const handleContentReady = React.useCallback(
    (e) => {
      if (!e.component || expandedRowKeys.length > 0) return;

      const data = productsRef.current || [];
      const rootKeys = data
        .filter((p) => Number(p.parentId) === 0)
        .map((p) => p.guid);

      if (rootKeys.length === 0) return;

      // Expand after render is 100% complete
      setTimeout(
        () => {
          try {
            rootKeys.forEach((key) => e.component.expandRow(key));
            setExpandedRowKeys(rootKeys);
          } catch (err) {
            console.warn('Row expansion failed:', err);
          }
        },
        DELAY
      ); // small delay to ensure DOM stabilization
    },
    [expandedRowKeys.length]
  );

  const createAndValidateFastLine = React.useCallback(
    async (ProductId: any, query: ICreateFastLineFacade) => {
      const response = await createFastLineMutation.mutateAsync({
        ...fastLineInlineData,
        ...query,
        WorksaleId: worksaleData?.WorksaleId,
        ProductId,
        ProductCatalogueType: 'autoi',
      });

      validate(null, {
        Forms: response.Forms,
        BomCode: selectedBomCode,
        KitDetail: response.GridLineDetails?.inlineObject?.KitDetail,
        KitStatus: response.GridLineDetails?.inlineObject?.KitStatus,
        OverrideDetails: response.GridLineDetails?.inlineObject?.OverrideDetails,
      });

      if (response.Status && !response.Forms) {
        setPriceOverrideDialogOpen(false);
        notifyProductAdded();
      }

      if (!response.Status) {
        notifyProductFailedToAdd();
      }

      if (!isPriceOverrideScreenRequest(response.Forms?.[0])) {
        // productGridRef.current?.reloadLines();
      }

      return response;
    },
    [
      worksaleData?.WorksaleId,
      selectedBomCode,
      fastLineInlineData,
      notifyProductAdded,
      notifyProductFailedToAdd,
    ]
  );

  const addLine = React.useCallback(
    async (ProductId: any, Quantity: any) => {
      try {
        const validationResponse = await validateProductMutation.mutateAsync({
          CustomerId: worksaleData?.Summary?.SaleHeader?.CustomerId,
          ProductId,
          SalesEntity: worksaleData?.Summary?.SaleHeader?.SalesEntity,
          WarehouseEntity: worksaleData?.Summary?.SaleHeader?.WarehouseEntity,
        });

        if (!validationResponse?.Status) return;

        if (!Boolean(ProductId)) {
          notifyProductFailedToAdd();

          return;
        }

        if (allowPriceQuantityDialogAutoi) {
          setAddToSaleOpen(true);

          return;
        }

        const response = await calculateFastLineDetailMutation.mutateAsync({
          WorksaleId: worksaleData?.WorksaleId,
          ProductId,
          OrderedQuantity: Quantity,
          ProductCatalogueType: 'autoi'
        });

        if (
          (response?.FastLineDetails?.BomCode && isNull(response?.FastLineDetails?.KitStatus)) ||
          isScreenRequest(response.Forms)
        ) {
          return;
        }

        await createAndValidateFastLine(ProductId, {
          ...response?.FastLineDetails,
          OrderedQuantity: Quantity,
        });
      } catch (err) {
        console.error(err);
        notifyProductFailedToAdd();
      }
    },
    [
      worksaleData,
      calculateFastLineDetailMutation,
      validateProductMutation,
      createAndValidateFastLine,
      allowPriceQuantityDialogAutoi,
      notifyProductAdded,
      notifyProductFailedToAdd,
    ]
  );

  // ----------------------
  // Cell renderers and handlers - stable callbacks
  // ----------------------
  const renderNameCell = React.useCallback(
    (cellData: any) => {
      const { data, value } = cellData;
      const showImage = data.parentId !== 0;

      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ display: 'flex' }}>
            <div style={{ marginRight: '10px', fontSize: '17px', color: '#000', textTransform: 'uppercase' }}>
              <span>{data?.ChildPurposeDescription}</span>
            </div>
          </div>
          {showImage && data.image ? (
            <img src={data?.image} alt={data?.type || data?.name || ''} style={{ width: 60, height: 60, marginRight: 10 }} />
          ) : showImage && !data.image ? (
            <div style={{ width: 60, height: 60, marginRight: 10, background: '#dddddd' }} />
          ) : null}
          {data.parentId === 0 ? (
            <div style={{ display: 'flex', whiteSpace: 'nowrap' }}>
              <span>{value}</span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start' }}>
              {data?.ProductId && data?.IsValidProduct ? (
                <span
                  style={{ color: '#009DDC', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => window.open(`/inventory-enquiry/product-details?ProductId=${data.ProductId}`, '_blank')}
                >
                  {data?.PartNumber}
                </span>
              ) : (
                <span style={{ fontSize: '16px', fontWeight: 'bold' }}>{data?.PartNumber}</span>
              )}
              <span>{data?.Brand}</span>
            </div>
          )}
        </div>
      );
    },
    []
  );

  const currencyCellRender = React.useCallback(
    (cellData: any) => {
      const { data, value } = cellData;

      return (
        <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', justifyContent: 'center' }}>
          <span style={{ fontWeight: 'bold' }}>
            {cellData.column.dataField === 'retail' ? 'Retail' : cellData.column.dataField === 'price' ? 'Price' : ''}
          </span>
          <span style={{ fontWeight: 'bold' }}>{data.parentId === 0 ? '' : `$${value || 0}`}</span>
        </div>
      );
    },
    []
  );

  const availabilityCellRender = React.useCallback(
    (cellData: any) => {
      const { data, value } = cellData;

      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {data.parentId !== 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
              <span>Local: {value || '0'}</span>
              <span>{`Other: ${data.other || '0'}`}</span>
            </div>
          )}
        </div>
      );
    },
    []
  );

  const descriptionCellRender = React.useCallback(
    (cellData: any) => {
      const { data } = cellData;

      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {data.parentId !== 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
              <span style={{ fontWeight: 'bold' }}>{data.Footnote}</span>
              <span>{data.Grade}</span>
            </div>
          )}
        </div>
      );
    },
    []
  );

  // ----------------------
  // onCellPrepared - stable callback (keeps your DOM manipulations)
  // ----------------------
  const onCellPrepared = React.useCallback(
    (e: any) => {
      const { IsValidProduct } = e.data;

      // Hide price and availability cells if not valid
      if (['price', 'availability'].includes(e.column.dataField)) {
        if (!IsValidProduct) {
          e.cellElement.innerHTML = '';
        }
      }

      // Add grayish background for level 1 rows
      if (e.rowType === 'data' && e.row.level === 0) {
        e.cellElement.style.backgroundColor = '#f5f5f5';
        if (e.column.dataField === 'description' || e.column.dataField === 'retail' || e.column.dataField === 'price' || e.column.dataField === 'local' || e.column.dataField === 'actions') {
          e.cellElement.innerHTML = '';
        }
      }

      // Extending the name column to adjust a long title mixed with whitespace property
      if (e.rowType === 'data' && e.column.dataField === 'name' && e.row.level === 0) {
        e.cellElement.colSpan = NAME_COLSPAN;
        // Hide the next column header
        const nextHeader = e.cellElement.nextElementSibling;
        if (nextHeader) {
          nextHeader.style.display = 'none';
        }
      }

      // Hide default expand buttons for level 2+ items
      if (e.rowType === 'data' && e.row.level > 0) {
        const expandElement = e.cellElement.querySelector('.dx-treelist-expanded, .dx-treelist-collapsed');
        if (expandElement) {
          expandElement.style.display = 'none';
        }
      }

      if (e.rowType === 'data' && e.column.dataField === 'availability' && e.data.hasChildren && e.row.level > 0 && e.data.ShowPlaceholder) {
        e.cellElement.textContent = e.data.Placeholder;
        e.cellElement.style.textAlign = 'center';
        e.cellElement.style.whiteSpace = 'nowrap';
        e.cellElement.style.overflow = 'visible';

        // Hide subsequent cells in the row
        const row = e.cellElement.parentNode;
        const cellIndex = e.cellIndex;
        for (let i = cellIndex + 1; i <= cellIndex + MAX_CELL_INDEX; i++) {
          if (row.cells[i]) {
            row.cells[i].style.display = 'none';
          }
        }
      }

      if (e.rowType === 'data' && e.column.dataField === 'price' && e.data.hasChildren && e.row.level > 0 && e.data.ShowPlaceholder) {
        e.cellElement.textContent = '';
      }

      // Add custom expand button to local column for level 2+ items
      if (e.rowType === 'data' && e.column.dataField === 'children' && e.row.level > 0 && e.data.hasChildren) {
        const container = e.cellElement;

        // style the cell
        e.cellElement.style.color = '#f47920';
        e.cellElement.style.fontSize = '15px';
        e.cellElement.style.fontWeight = 'bold';

        // Create expand/collapse button
        const expandButton = document.createElement('div');
        expandButton.className = 'custom-expand-button';

        // Use a single icon type that will rotate
        const iconElement = document.createElement('span');
        iconElement.className = e.row.isExpanded ? 'dx-icon  dx-icon-spindown' : 'dx-icon dx-icon-spinright';
        expandButton.appendChild(iconElement);

        expandButton.onclick = (ev) => {
          ev.stopPropagation();
          ev.preventDefault();
          const treeList = e.component;
          if (e.row.isExpanded) {
            setTimeout(
              () => {
                treeList.collapseRow(e.key);
                if (e.row.isExpanded) {
                  treeList.collapseRow(e.key);
                }
              },
              EXPAND_DELAY
            );
          } else {
            treeList.expandRow(e.key);
          }
        };
        container.appendChild(expandButton);
      }

      // Hide actions unless valid and leaf node
      if (e.column.dataField === 'actions') {
        // Always clear previous content
        e.cellElement.innerHTML = '';

        if (IsValidProduct && e.row.level > 0) {
          const container = e.cellElement;
          container.style.padding = '0';
          container.style.boxSizing = 'border-box';

          const wrapper = document.createElement('div');
          wrapper.className = 'action-wrapper';
          wrapper.style.display = 'flex';
          wrapper.style.alignItems = 'center';
          wrapper.style.justifyContent = 'flex-start';
          wrapper.style.width = '100%';
          wrapper.style.height = '100%';
          wrapper.style.boxSizing = 'border-box';
          wrapper.style.padding = '6px 8px';

          const textValue = document.createElement('input');
          textValue.type = 'number';
          textValue.min = '1';
          const currValue = quantityMapRef.current[e.data.ProductId] ?? e.data.PCQ ?? 1;
          textValue.value = currValue;
          textValue.className = 'action-text';

          const minusButton = document.createElement('button');
          minusButton.textContent = '-';
          minusButton.className = 'action-button';

          const plusButton = document.createElement('button');
          plusButton.textContent = '+';
          plusButton.className = 'action-button';

          const addButton = document.createElement('button');
          addButton.textContent = 'Add';
          addButton.className = 'add-button';
          addButton.disabled = (Number(textValue.value) || 0) <= 0;

          // Function to update state of buttons dynamically
          const updateControls = () => {
            const currentValue = Number(textValue.value);
            addButton.disabled = !textValue.value || isNaN(currentValue) || currentValue <= 0;
          };

          // Input change handler
          textValue.oninput = (event) => {
            const value = (event.target as HTMLInputElement).value;
            const numericValue = Number(value);
            // Allow clearing or any positive value except 0
            if (value === '' || (!isNaN(numericValue) && numericValue > 0)) {
              quantityMapRef.current[e.data.ProductId] = numericValue;
            }
            updateControls();
          };

          // Enforce min 1 only on blur
          textValue.onblur = () => {
            const numericValue = Number(textValue.value);
            if (!textValue.value || isNaN(numericValue) || numericValue <= 0) {
              textValue.value = '1';
              quantityMapRef.current[e.data.ProductId] = 1;
            }
            updateControls();
          };

          // Minus button click
          minusButton.onclick = () => {
            let currentValue = Number(textValue.value) || 0;
            if (currentValue > 1) {
              currentValue--;
              textValue.value = currentValue.toString();
              quantityMapRef.current[e.data.ProductId] = currentValue;
            }
            updateControls();
          };

          // Plus button click
          plusButton.onclick = () => {
            let currentValue = Number(textValue.value) || 0;
            currentValue++;
            textValue.value = currentValue.toString();
            quantityMapRef.current[e.data.ProductId] = currentValue;
            updateControls();
          };

          // Add button click
          addButton.onclick = () => {
            const quantity = Number(textValue.value);
            quantityMapRef.current[e.data.ProductId] = quantity;
            setSelectedRowDetails({
              ...e.data,
              Quantity: quantity,
            });
            addLine(e.data.ProductId, quantity);
          };

          wrapper.appendChild(minusButton);
          wrapper.appendChild(textValue);
          wrapper.appendChild(plusButton);
          wrapper.appendChild(addButton);

          container.appendChild(wrapper);
        }
      }
    },
    [addLine]
  );

  const detailClickHandler = React.useCallback(
    (cellData: any) => {
      // Save current scroll position and selected row before navigating
      saveScrollPosition(cellData?.data?.guid);
      handleVehicleDetailsOnClick(cellData);
    },
    [handleVehicleDetailsOnClick, saveScrollPosition]
  );

  const DetailCellComponent = React.useCallback(
    (cellData: any) => {
      if (cellData.data.rowType !== 'data' || cellData.data.row.level === 0) return null;

      return <MoreIcon className='line-details' onClick={() => detailClickHandler(cellData)} />;
    },
    [detailClickHandler]
  );

  // ----------------------
  // onRowExpanding - use productsRef + DataSource to avoid setState re-renders
  // ----------------------
  const onRowExpanding = React.useCallback(
    async (e: any) => {
      const node = e.component.getNodeByKey(e.key);

      // Only refetch if node is not cached
      if (expandedNodes.has(node.key)) {
        return;
      }

      if (node.level > 0) {
        const data = await fetchCataloguePartsChildrenListMutation.mutateAsync({
          CatalogueType: CatalogueTypeId,
          ItemId: node.data?.ItemId,
          VehicleId: VehicleId,
          CatalogueId: selectedCatalogueEntry?.PartGroupID || 0,
          urlQueryParams: {
            SubGroupId: selectedCatalogueEntry?.SubGroupId || 0,
          },
        });

        const children = data?.Parts ?? [];

        if (children.length > 0) {
          // Avoid duplicates when merging
          const existingIds = new Set(productsRef.current.map((p: any) => p.guid));
          const newChildren = children.filter((c: any) => !existingIds.has(c.guid));

          if (newChildren.length > 0) {
            const merged = [...productsRef.current, ...newChildren];
            refreshDataSource(merged);
          }

          // Price enrichments for newly loaded children
          (async () => {
            const pricingRequest = children.filter((c: any) => c.ProductId).map((c: any) => ({ ProductId: c.ProductId, Quantity: c.PCQ || 1 }));

            if (pricingRequest.length > 0) {
              const pricingResponse = await fetchPricingAndAvailabilityMutation.mutateAsync({
                ProductList: pricingRequest,
                urlQueryParams: { CustomerId: customerId, Warehouse: worksaleData?.Summary?.SaleHeader?.WarehouseEntity, WorksaleId: worksaleData?.WorksaleId },
              });

              const pricingMap = new Map((pricingResponse?.ProductDetails ?? []).map((p: any) => [String(p.ProductId), p]));

              const enrichedChildren = children.map((child: any) => {
                const pricingData = pricingMap.get(String(child.ProductId)) as any;

                return {
                  ...child,
                  price: pricingData?.ExtendedIncDisplay ?? null,
                  retail: pricingData?.RetailPriceDisplay ?? null,
                  other: pricingData?.ElsewhereQuantityDisplay ?? null,
                  availability: pricingData?.AvailableQuantityDisplay ?? null,
                  IsValidProduct: pricingData?.IsValidProduct ?? false,
                };
              });

              // Merge enriched children back into existing items
              const mergedAfterEnrich = productsRef.current.map((p: any) => enrichedChildren.find((en: any) => en.ItemId === p.ItemId) ?? p);
              refreshDataSource(mergedAfterEnrich);
            }
          })();
        }

        setExpandedNodes((prev) => new Set(prev).add(node.key));
        setExpandedRowKeys((prev) => [...prev, e.key]);
      }
    },
    [
      expandedNodes,
      CatalogueTypeId,
      VehicleId,
      selectedCatalogueEntry,
      customerId,
      worksaleData?.Summary,
      refreshDataSource,
    ]
  );

  const onRowCollapsed = React.useCallback(
    (e: any) => {
      // Remove from cache so next expand will refetch
      setExpandedNodes((prev) => {
        const newSet = new Set(prev);
        newSet.delete(e.key);

        return newSet;
      });
    },
    []
  );

  const handleAddToSaleOnApply = React.useCallback(
      (query) => {
          return createAndValidateFastLine(
              selectedRowDetails?.ProductId,
              {
              ...query,
              BomCode: selectedBomCode,
              KitStatus: kitStatus,
          });
      },
      [createFastLineMutation.data, selectedBomCode, kitStatus, createAndValidateFastLine]
  );

  const calculateAndValidateFastLine = React.useCallback(
    (query: ICalculateFastLineCriteriaFacade) => {
        return calculateFastLineDetailMutation.mutateAsync({
          WorksaleId: worksaleData?.WorksaleId,
          BomCode: selectedBomCode,
          KitStatus: kitStatus,
          ...query,
          ProductCatalogueType: 'autoi'
        })
        .then((response) => {
          validate(null, {
              Forms: response.Forms,
              BomCode: selectedBomCode,
              KitDetail: response.FastLineDetails?.KitDetail,
              KitStatus: response.FastLineDetails?.KitStatus,
              OverrideDetails: response.FastLineDetails,
          });
          if (response?.FastLineDetails?.BomCode) {
              setSelectedBomCode(response?.FastLineDetails?.BomCode);
          }
          if (response?.FastLineDetails?.KitStatus) {
              setKitStatus(response?.FastLineDetails?.KitStatus);
          }

          return response;
      });
    },
    [worksaleData?.WorksaleId]
  );

  const handleAddToLineClose = () => {
    setAddToSaleOpen(false);
    setSelectedRowDetails(null);
  };

  const handlePriceOverrideOk = React.useCallback(
      async (priceOverrideData: IPriceOverrideData) => {
          try {
              const response = await createAndValidateFastLine(
                createFastLineMutation.data?.GridLineDetails?.inlineObject?.ProductId,
                {
                  ...createFastLineMutation.data?.GridLineDetails.inlineObject,
                  BomCode: selectedBomCode,
                  KitStatus: kitStatus,
                  DiscountedPrice: createFastLineMutation.data?.GridLineDetails.inlineObject?.Price,
                  OverrideDetails: { ...priceOverrideDetailSummary, ...priceOverrideData },
              });
              if (response.Status && !response.Forms) {
                  setAddToSaleOpen(false);
                  setPriceOverrideDetailSummary(null);
                  setSelectedRowDetails(null);
              }
          } catch (err) {
              console.warn(err);
          }
      },
      [createFastLineMutation.data, priceOverrideDetailSummary, selectedBomCode, kitStatus, createAndValidateFastLine]
  );

  const handlePriceOverrideCancel = React.useCallback(
      () => {
          setPriceOverrideDialogOpen(false);
      },
      []
  );

  const handleActionClick = React.useCallback(
          async (status: 'c' | 'k') => {
              setMultipleBomState(false);
              setKitStatus(status);

              if (status) {
                  if (status === 'c') {
                      addSaleRef.current?.disablePriceField();
                  }

                  calculateAndValidateFastLine({
                      ProductId: createFastLineMutation.data?.GridLineDetails?.inlineObject?.ProductId,
                      OrderedQuantity: fastLineInlineData?.OrderedQuantity,
                      KitStatus: status
                  });
              } else {
                  addSaleRef.current?.resetToDefault();
              }

          },
          [calculateFastLineDetailMutation.data, addSaleRef.current, selectedBomCode, createAndValidateFastLine, calculateAndValidateFastLine]
      );

  return (
    <div className='product-grid-container'>
      <TreeList
        ref={treeListRef}
        className='no-gridlines'
        noDataText=''
        dataSource={dataSource}
        keyExpr='guid'
        parentIdExpr='parentId'
        // hasItemsExpr='hasChildren'
        showBorders={false}
        showColumnHeaders={false}
        columnAutoWidth={false}
        columnResizingMode='nextColumn'
        onContentReady={handleContentReady}
        onCellPrepared={onCellPrepared}
        expandedRowKeys={expandedRowKeys}
        onExpandedRowKeysChange={setExpandedRowKeys}
        autoExpandAll={false}
        scrolling={{
          mode: 'standard',
          showScrollbar: 'always',
          useNative: true
        }}
        onRowPrepared={(e) => {
          if (e.rowType === 'data' && e.data?.Indent) {
            e.rowElement.classList.add('has-indent');
          }
        }}
        onRowExpanding={onRowExpanding}
        onRowCollapsed={onRowCollapsed}
      >
        <Column
          caption='Details'
          name='details'
          allowEditing={false}
          width={COLUMN_SIZE.sm0}
          allowResizing={false}
          fixed={true}
          alignment='center'
          allowHeaderFiltering={false}
          cellComponent={DetailCellComponent}
        />
        <Column dataField='name' cellRender={renderNameCell} minWidth={COLUMN_SIZE.xl} />
        <Column dataField='children' width={COLUMN_SIZE.sm1} />
        <Column dataField='description' minWidth={COLUMN_SIZE.md1} cellRender={descriptionCellRender} />
        <Column dataField='availability' minWidth={COLUMN_SIZE.md1} cellRender={availabilityCellRender} />
        <Column dataField='price' format='currency' width={COLUMN_SIZE.sm1} cellRender={currencyCellRender} />
        <Column dataField='actions' width={COLUMN_SIZE.lg2} />
      </TreeList>

      <LoadPanel
        visible={fetchCataloguePartsListMutation?.isLoading}
        shading={true}
        showIndicator={true}
        showPane={true}
        position={{
          of:
            treeListRef.current && treeListRef.current.instance && typeof treeListRef.current.instance.element === 'function'
              ? treeListRef.current.instance.element()
              : undefined,
        }}
        height={DATA_GRID_LOADING_INDICATOR_HEIGHT_WIDTH}
        width={DATA_GRID_LOADING_INDICATOR_HEIGHT_WIDTH}
      />
      <PriceOverrideAuthorityDialog
          open={priceOverrideDialogOpen}
          loading={createFastLineMutation.isLoading}
          type={priceOverrideScreenType}
          data={priceOverrideDetailSummary}
          onOk={handlePriceOverrideOk}
          onCancel={handlePriceOverrideCancel}
      />
      <BOMDialog
          open={multipleBomOpen}
          onClose={() => setMultipleBomState(false)}
          data={bomList}
          kitStatus={kitStatus ? kitStatus.toLowerCase() : ''}
          onActionClick={handleActionClick}
          onSelectedRowChanged={setSelectedBomCode}
      />
      {addToSaleOpen && <AddToSale
        innerRef={addSaleRef}
        isAddToSaleOpen={addToSaleOpen}
        productId={selectedRowDetails?.ProductId}
        productCode={(fastLineInlineData as any)?.ProductIdDisplay || selectedRowDetails?.PartNumber || ''}
        productName={(fastLineInlineData as any)?.ProductIdLabel}
        productPrice={fastLineInlineData?.DiscountedPrice}
        priceDescription={fastLineInlineData?.PriceDescription}
        productQuantity={selectedRowDetails?.Quantity}
        calculateLoading={calculateFastLineDetailMutation.isLoading}
        handleClose={handleAddToLineClose}
        calculateFastLine={calculateAndValidateFastLine}
        onApply={handleAddToSaleOnApply}
      />}
    </div>
  );
};

export default Catalogue;
